import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Phone, Quote } from "lucide-react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { apiClient } from "@/lib/api";
import { DynamicIcon } from "@/components/shared/IconMap";
import { Breadcrumbs } from "@/components/shared/Breadcrumbs";
import { Watermark } from "@/components/shared/Watermark";
import { CTAButton } from "@/components/shared/CTAButton";
import { MotionReveal, MotionStagger, MotionStaggerItem } from "@/components/shared/MotionReveal";
import { CaseEvaluationDialog } from "@/components/home/CaseEvaluationDialog";
import { SEO, buildFAQSchema, buildBreadcrumbSchema, buildLegalServiceSchema } from "@/components/seo/SEO";
import { FIRM, PRACTICE_AREA_IMAGES } from "@/constants/firmInfo";
import { useLanguage } from "@/i18n/LanguageContext";

const initials = (name = "") =>
  name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

export default function PracticeAreaDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [area, setArea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [proof, setProof] = useState(null);
  const { t, getPracticeAreaOverride } = useLanguage();

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    apiClient
      .get(`/practice-areas/${slug}`)
      .then((res) => setArea(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!area) return;
    apiClient
      .get("/testimonials")
      .then((res) => {
        const match = res.data.find((item) => item.practice_area === area.title) || res.data[0];
        setProof(match || null);
      })
      .catch(() => {});
  }, [area]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="practice-area-not-found">
        <h1 className="font-heading text-2xl font-semibold text-[color:var(--navy-950)]">Practice Area Not Found</h1>
        <p className="mt-3 text-slate-600">We couldn&apos;t find that practice area.</p>
        <div className="mt-6 flex justify-center">
          <CTAButton variant="primary" onClick={() => navigate("/")}>Return Home</CTAButton>
        </div>
      </div>
    );
  }

  if (loading || !area) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <div className="h-8 w-8 border-2 border-[color:var(--gold-500)] border-t-transparent rounded-full animate-spin" data-testid="practice-area-loading-spinner" />
      </div>
    );
  }

  // Spanish overlay for title/tagline/overview/services only (core-page
  // translation scope). FAQs, process steps, and the testimonial proof band
  // intentionally stay in English regardless of language.
  const override = getPracticeAreaOverride(area.slug);
  const displayTitle = override?.title || area.title;
  const displayTagline = override?.heroTagline || area.hero_tagline;
  const displayOverview = override?.overview || area.overview;
  const displayServices = override?.services || area.services;

  const heroImage = PRACTICE_AREA_IMAGES[area.slug] || PRACTICE_AREA_IMAGES["family-immigration"];
  const watermarkWord = displayTitle.split(" ")[0];
  const whyChooseUsItems = t("practiceAreaDetail.whyChooseUsItems");
  const ctaSuffix = t("practiceAreaDetail.ctaHeadingSuffix");

  return (
    <article data-testid="practice-area-detail-page">
      <SEO
        title={override?.metaTitle || area.meta_title}
        description={override?.metaDescription || area.meta_description}
        path={`/practice-areas/${area.slug}`}
        jsonLd={[
          buildLegalServiceSchema(),
          buildFAQSchema(area.faqs),
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Practice Areas", path: "/" },
            { name: area.title, path: `/practice-areas/${area.slug}` },
          ]),
        ]}
      />

      {/* Hero */}
      <header className="relative bg-[color:var(--navy-950)] hero-noise overflow-hidden">
        <Watermark word={watermarkWord} position="top-right" className="text-white top-2" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(900px 500px at 85% 0%, rgba(184,154,61,0.14), transparent 55%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <Breadcrumbs
            items={[
              { label: t("practiceAreaDetail.breadcrumbHome"), to: "/" },
              { label: t("practiceAreaDetail.breadcrumbPracticeAreas"), to: "/" },
              { label: displayTitle, to: `/practice-areas/${area.slug}` },
            ]}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20 pt-8 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="h-14 w-14 rounded-2xl bg-white/10 grid place-items-center mb-5">
              <DynamicIcon name={area.icon} className="h-7 w-7 text-[color:var(--gold-500)]" />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-white text-balance">{displayTitle}</h1>
            <p className="mt-4 text-slate-300 max-w-xl text-base sm:text-lg leading-relaxed">{displayTagline}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <CaseEvaluationDialog
                trigger={<CTAButton variant="brass" data-testid="practice-area-hero-cta-button">{t("practiceAreaDetail.heroCta")}</CTAButton>}
              />
              <a href={`tel:${FIRM.phoneTel}`} data-testid="practice-area-hero-phone-link">
                <CTAButton variant="outline" icon={Phone}>{FIRM.phoneDisplay}</CTAButton>
              </a>
            </div>
          </motion.div>
          <motion.div
            className="lg:col-span-5 relative hidden sm:block"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
              <img src={heroImage} alt={`${displayTitle} legal services`} className="w-full h-[340px] object-cover" loading="eager" />
            </div>
            <div className="absolute -bottom-5 -left-5 h-16 w-16 rounded-full bg-[color:var(--gold-500)] text-[color:var(--navy-950)] grid place-items-center shadow-[0_10px_30px_rgba(184,154,61,0.35)] -rotate-6">
              <DynamicIcon name={area.icon} className="h-6 w-6" />
            </div>
          </motion.div>
        </div>
      </header>

      {/* Overview */}
      <section className="py-16 sm:py-20 bg-[color:var(--paper)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
          <MotionReveal className="lg:col-span-8">
            <span className="text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-500)]">{t("practiceAreaDetail.overviewEyebrow")}</span>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl text-[color:var(--navy-950)] mb-5">{t("practiceAreaDetail.overviewHeadingPrefix")} {displayTitle}</h2>
            {displayOverview.map((p) => (
              <p key={p} className="text-slate-600 leading-7 mb-4">{p}</p>
            ))}
          </MotionReveal>
          <MotionReveal delay={0.1} className="lg:col-span-4">
            <div className="rounded-3xl bg-[color:var(--sand)] border border-slate-200/70 p-7 sticky top-24">
              <h3 className="font-heading text-lg text-[color:var(--navy-950)] mb-4">{t("practiceAreaDetail.whyChooseUs")}</h3>
              <ul className="space-y-3">
                {whyChooseUsItems.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <CheckCircle2 className="h-4 w-4 text-[color:var(--gold-500)] mt-0.5 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <a href={`tel:${FIRM.phoneTel}`} data-testid="practice-area-sidebar-phone-link">
                  <CTAButton variant="primary" icon={Phone} className="w-full justify-center">{FIRM.phoneDisplay}</CTAButton>
                </a>
              </div>
            </div>
          </MotionReveal>
        </div>
      </section>

      {/* Services */}
      <section className="relative py-16 sm:py-20 bg-[color:var(--sand)] overflow-hidden">
        <Watermark word="Services" position="bottom-left" className="text-[color:var(--navy-950)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MotionReveal className="max-w-2xl mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl text-[color:var(--navy-950)]">{t("practiceAreaDetail.servicesHeading")}</h2>
          </MotionReveal>
          <MotionStagger className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {displayServices.map((s) => (
              <MotionStaggerItem key={s.title}>
                <div className="group rounded-3xl border border-slate-200/70 bg-white p-7 h-full shadow-[0_10px_30px_rgba(11,18,32,0.05)] hover:shadow-[0_18px_50px_rgba(11,18,32,0.12)] hover:-translate-y-1 transition-all duration-300">
                  <div className="h-11 w-11 rounded-2xl bg-[color:var(--navy-950)] grid place-items-center mb-4 group-hover:bg-[color:var(--gold-500)] transition-colors duration-300">
                    <CheckCircle2 className="h-5 w-5 text-[color:var(--gold-400)] group-hover:text-[color:var(--navy-950)] transition-colors duration-300" />
                  </div>
                  <h3 className="font-heading text-lg text-[color:var(--navy-950)] mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.description}</p>
                </div>
              </MotionStaggerItem>
            ))}
          </MotionStagger>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 sm:py-20 bg-[color:var(--paper)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <MotionReveal>
            <h2 className="font-heading text-2xl sm:text-3xl text-[color:var(--navy-950)] mb-12">Our Process</h2>
          </MotionReveal>
          <MotionStagger className="relative pl-10 before:absolute before:left-4 before:top-2 before:bottom-2 before:w-px before:bg-slate-200">
            {area.process_steps.map((step) => (
              <MotionStaggerItem key={step.step} className="relative pb-9 last:pb-0">
                <span className="absolute -left-10 top-0 h-8 w-8 rounded-full bg-[color:var(--navy-950)] text-[color:var(--gold-400)] grid place-items-center text-sm font-heading font-semibold ring-4 ring-white" data-testid="process-step">
                  {step.step}
                </span>
                <h3 className="font-heading text-lg text-[color:var(--navy-950)] mb-1.5">{step.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
              </MotionStaggerItem>
            ))}
          </MotionStagger>
        </div>
      </section>

      {/* Proof band */}
      {proof && (
        <section
          className="relative py-14 sm:py-16 overflow-hidden"
          style={{ background: "linear-gradient(120deg, #0F766E 0%, #0d5a54 100%)" }}
          data-testid="practice-area-proof-band"
        >
          <MotionReveal className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Quote className="h-8 w-8 text-white/70 mx-auto mb-4" />
            <p className="font-heading text-xl sm:text-2xl text-white italic leading-snug">&ldquo;{proof.quote}&rdquo;</p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Avatar className="h-10 w-10 ring-2 ring-white/30">
                <AvatarFallback className="bg-white/15 text-white font-heading text-sm">{initials(proof.name)}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-semibold text-white/90">{proof.name}</span>
            </div>
          </MotionReveal>
        </section>
      )}

      {/* FAQ */}
      <section className="relative py-16 sm:py-20 bg-[color:var(--paper-2)] overflow-hidden">
        <Watermark word="FAQ" position="top-right" className="text-[color:var(--navy-950)]" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <MotionReveal>
            <h2 className="font-heading text-2xl sm:text-3xl text-[color:var(--navy-950)] mb-6">Frequently Asked Questions</h2>
          </MotionReveal>
          <Accordion type="single" collapsible className="bg-white rounded-3xl border border-slate-200 px-6 shadow-[0_10px_30px_rgba(11,18,32,0.05)]" data-testid="faq-accordion">
            {area.faqs.map((faq, idx) => (
              <AccordionItem key={faq.question} value={`item-${idx}`}>
                <AccordionTrigger className="text-left font-medium text-[color:var(--navy-950)]" data-testid={`faq-question-${idx}`}>
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-[color:var(--navy-950)] hero-noise relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(900px 500px at 50% 0%, rgba(184,154,61,0.14), transparent 60%)" }}
        />
        <MotionReveal className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl text-white">
            {t("practiceAreaDetail.ctaHeadingPrefix")} {displayTitle}{ctaSuffix ? ` ${ctaSuffix}` : ""}
          </h2>
          <p className="mt-3 text-slate-300">{t("practiceAreaDetail.ctaSubtitle")}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <CaseEvaluationDialog
              trigger={<CTAButton variant="brass">{t("practiceAreaDetail.ctaPrimary")}</CTAButton>}
            />
            <Link to="/contact" data-testid="practice-area-cta-contact-link">
              <CTAButton variant="outline">{t("practiceAreaDetail.ctaSecondary")}</CTAButton>
            </Link>
          </div>
        </MotionReveal>
      </section>
    </article>
  );
}
